export type FileHandler<T extends ProcessResult = ProcessResult> = (params: {
  file: File;
  onUploadProgress: (progress: ProgressEvent) => void;
}) => Promise<T>;

export type FileKey = { type: 'fileKey'; fileKey: string; file?: File };

export type ProcessResult = FileKey;

export type FileHandlerCreator<T extends ProcessResult = ProcessResult> =
  (options?: {
    onSuccess?: (data: T) => void;
    onError?: (error: unknown) => void;
  }) => FileHandler<T>;

// ============================================================================
// Configuration Types
// ============================================================================

export type ExtensionHandlerMap = Map<string, FileHandler>;

export type FileHandlerGroup = {
  /** Array of file extensions (without leading dot) that should use this handler */
  extensions: Array<string>;
  /** Optional array of MIME types that should use this handler */
  mimeTypes?: Array<string>;
  /** The handler function to use for these file types */
  handler: FileHandler;
};

export type MimeTypeHandlerMap = Map<string, FileHandler>;

export class FileHandlerConfigBuilder {
  /** @private Internal storage for handler groups before building */
  private groups: Array<FileHandlerGroup> = [];

  /** @private Default handler to use when no handler is specified */
  private defaultHandler: FileHandler | null = null;

  /**
   * Set a default handler that will be used for extension groups
   * where no handler is explicitly specified
   */
  setDefaultHandler(handler: FileHandler): this {
    this.defaultHandler = handler;
    return this;
  }

  /**
   * Add a single handler with its associated extensions and MIME types
   */
  addHandler({
    extensions,
    handler,
    options,
  }: {
    extensions: Array<string> | string;
    handler: FileHandler;
    options?: { mimeTypes?: Array<string> };
  }): this {
    const extensionsArray = Array.isArray(extensions)
      ? extensions
      : [extensions];

    this.groups.push({
      extensions: extensionsArray,
      handler,
      mimeTypes: options?.mimeTypes,
    });

    return this;
  }

  /**
   * Add multiple extension groups that all use the same handler
   * This is useful when you have many file types that should be processed the same way
   *
   * @example
   * builder.addHandlerForGroups(uploadToS3Handler, [
   *   { extensions: ['pdf'], mimeTypes: ['application/pdf'] },
   *   { extensions: ['xlsx', 'xls'], mimeTypes: ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'] },
   *   { extensions: ['jpg', 'jpeg'], mimeTypes: ['image/jpeg'] },
   *   { extensions: ['png'], mimeTypes: ['image/png'] },
   * ])
   */
  addHandlerForGroups(
    handler: FileHandler,
    groups: Array<{
      extensions: Array<string> | string;
      mimeTypes?: Array<string>;
    }>
  ): this {
    for (const group of groups) {
      this.addHandler({
        extensions: group.extensions,
        handler,
        options: { mimeTypes: group.mimeTypes },
      });
    }
    return this;
  }

  buildExtensionMap(): ExtensionHandlerMap {
    const config: ExtensionHandlerMap = new Map<string, FileHandler>();

    for (const group of this.groups) {
      for (const extension of group.extensions) {
        if (config.has(extension)) {
          console.warn(
            `FileHandlerConfig: Extension "${extension}" is defined multiple times. The last definition will be used.`
          );
        }
        config.set(extension, group.handler);
      }
    }

    return config;
  }

  buildMimeTypeMap(): MimeTypeHandlerMap {
    const map = new Map<string, FileHandler>();

    for (const group of this.groups) {
      if (group.mimeTypes) {
        for (const mimeType of group.mimeTypes) {
          // Normalize MIME type (lowercase, remove parameters)
          const normalizedMimeType = mimeType
            .toLowerCase()
            .split(';')[0]
            .trim();
          map.set(normalizedMimeType, group.handler);
        }
      }
    }

    return map;
  }

  buildAllMimeTypes(): Set<string> {
    return new Set(
      [...this.buildMimeTypeMap().keys()].filter(
        (type): type is string => type !== undefined
      )
    );
  }

  build(): {
    extensionMap: ExtensionHandlerMap;
    mimeTypeMap: MimeTypeHandlerMap;
    allMimeTypes: Set<string>;
    commonExtensions: Set<string>;
    allExtensions: Set<string>;
  } {
    return {
      extensionMap: this.buildExtensionMap(),
      mimeTypeMap: this.buildMimeTypeMap(),
      allMimeTypes: this.buildAllMimeTypes(),
      commonExtensions: this.buildCommonExtensions(),
      allExtensions: this.buildAllExtensions(),
    };
  }

  buildAllExtensions(): Set<string> {
    return new Set(
      this.groups
        .map((group) => group.extensions)
        .flat()
        .filter((ext): ext is string => ext !== undefined)
    );
  }

  buildCommonExtensions(): Set<string> {
    return new Set(
      this.groups
        .map((group) => group.extensions[0])
        .filter((ext): ext is string => ext !== undefined)
    );
  }
}

// ============================================================================
// FileHandler Creation Utilities
// ============================================================================

// Overload to allow type inference from callback
export function createFileHandler<T extends ProcessResult>(
  handler?: (options?: {
    onSuccess?: (data: T) => void;
    onError?: (error: unknown) => void;
  }) => FileHandler
): FileHandlerCreator<T>;

// Default overload for when type can't be inferred
export function createFileHandler<T extends ProcessResult = ProcessResult>(
  handler: FileHandlerCreator<T>
): FileHandlerCreator<T>;

export function createFileHandler<T extends ProcessResult = ProcessResult>(
  handler:
    | FileHandlerCreator<T>
    | ((options: {
        onSuccess: (data: T) => void;
        onError: (error: unknown) => void;
      }) => FileHandler)
): FileHandlerCreator<T> {
  return handler as FileHandlerCreator<T>;
}

export interface FileHandlerConfig {
  extensionMap: ExtensionHandlerMap;
  mimeTypeMap: MimeTypeHandlerMap;
  allMimeTypes: Set<string>;
  commonExtensions: Set<string>;
  allExtensions: Set<string>;
}
